import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Stack,
    Tooltip
} from "@mui/material";
import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {closeSnackbar, enqueueSnackbar} from "notistack";
import React, {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    serverTableControlIconSx,
    serverTableControlSx
} from "../../../common/serverTable/constants";
import {ServerTableExportDialog} from "../../../common/serverTable/export/ServerTableExportDialog";
import type {
    ServerTableExportColumn,
    ServerTableExportRun
} from "../../../common/serverTable/export/serverTableExport";
import {
    enqueueServerTableExportDone,
    enqueueServerTableExportStarted
} from "../../../common/serverTable/export/serverTableExportSnackbars";
import {useServerTableContext} from "../../../common/serverTable/ServerTableContext";
import {
    useServerTableToolbarContext,
    useServerTableToolbarItemContext
} from "../../../common/serverTable/ServerTableToolbarContext";
import {defineServerTableToolbarItem} from "../../../common/serverTable/ServerTableToolbarItem";
import {isAnswerableQuestion, useSurveyAnswerContext} from "../../shared/surveyAnswer.util";
import {SurveySensitiveMarker} from "../../shared/SurveyQuestionMarkers";
import {useSurveyExportAssets} from "../../shared/useSurveyExportAssets";
import {
    buildSurveySubmissionsDataset,
    surveySubmissionsDatasetColumnOptions,
    surveySubmissionsDatasetFileName,
    type SurveySubmissionsDatasetFormat
} from "./surveySubmissionsDataset";

interface Props {
    survey?: Survey
}

/**
 * Downloads the list as it stands - the submissions matching the current search, filters, and
 * sorting, one row per submission and one column per question, the shape a pivot table expects.
 * The shared export dialog picks the format, the columns and the rows (this page, a count from
 * the top, or all); the file is still built here in the browser from the table's own rows.
 * Columns per question only exist for one survey, so without `survey` the control stays disabled.
 */
const SurveySubmissionsExportComponent = ({survey}: Props) => {
    const {t} = useTranslation();
    const ctx = useSurveyAnswerContext();
    const {organizationName, branding} = useSurveyExportAssets(true);
    const {table, fetchAllRows, query} = useServerTableContext<AggregationSurveySubmissionListItem>();
    const {closePanel} = useServerTableToolbarContext();
    const toolbarItem = useServerTableToolbarItemContext();

    const [open, setOpen] = useState(false);
    const [running, setRunning] = useState<SurveySubmissionsDatasetFormat>();
    const [includeSensitive, setIncludeSensitive] = useState(false);

    const hasSensitiveQuestions = useMemo(
        () => (survey?.questions ?? []).some(question => (
            isAnswerableQuestion(question) && !!question.isSensitive
        )),
        [survey?.questions]
    );

    const columns = useMemo(
        () => surveySubmissionsDatasetColumnOptions({survey, ctx, t}),
        [ctx, survey, t]
    );
    // Every column, as the export always wrote: the table's own columns are not the dataset's.
    const allColumnIds = useMemo(() => columns.map(column => column.id), [columns]);

    const totalRowCount = query.data?.totalElements ?? 0;
    const pageRowCount = query.data?.content.length ?? 0;
    const {pageIndex, pageSize} = table.getState().pagination;

    const runExport = async (
        format: SurveySubmissionsDatasetFormat,
        {maxRows, offset, columns: picked}: ServerTableExportRun & {columns: ServerTableExportColumn[]}
    ) => {
        if (!fetchAllRows || running) return;

        setRunning(format);
        const progress = enqueueServerTableExportStarted(t, maxRows ?? totalRowCount, format);
        try {
            const now = new Date();
            // The table's own rows come back with sensitive answers withheld; ticking the box is what
            // asks the server for them, and only for this one request.
            const wantsSensitive = includeSensitive && hasSensitiveQuestions;
            const {rows, total} = await fetchAllRows(
                wantsSensitive ? {includeSensitive: true} : undefined,
                {offset, maxRows}
            );
            // What was asked for, so only the row cap - never the chosen slice - reads as "only the first n".
            const wanted = Math.min(maxRows ?? Infinity, Math.max(total - (offset ?? 0), 0));
            const dataset = buildSurveySubmissionsDataset({
                survey,
                submissions: rows,
                total: Math.max(wanted, rows.length),
                ctx,
                t,
                organizationName,
                branding,
                includeSensitive: wantsSensitive,
                now,
                columnIds: picked.map(column => column.id),
            });

            if (!dataset.rows.length) {
                enqueueSnackbar(t('No submissions match the current filters.'), {variant: 'info'});
                return;
            }

            const fileName = surveySubmissionsDatasetFileName(survey, format, now);
            const writers = await import('./surveySubmissionsWorkbook');

            if (format === 'xlsx') {
                await writers.downloadSurveySubmissionsWorkbook(dataset, fileName);
            } else {
                writers.downloadSurveySubmissionsCsv(dataset, fileName);
            }

            enqueueServerTableExportDone(t, fileName);
            if (dataset.truncatedLabel) {
                enqueueSnackbar(dataset.truncatedLabel, {variant: 'warning'});
            }
        } catch (error) {
            console.log(error);
            enqueueSnackbar(t('Could not complete this action. Please try again.'), {variant: 'error'});
        } finally {
            closeSnackbar(progress);
            setRunning(undefined);
        }
    };

    /*
        The list total the toolbar already holds is what an export would collect, so an empty one
        can only produce an error snackbar - the control says that up front instead.
     */
    const nothingToExport = !query.isLoading && !query.isError && !totalRowCount;
    const disabled = !survey || !!running || query.isLoading || query.isError || query.isPreviousData || nothingToExport;
    const reason = !survey
        ? t('Filter by one survey to export its answers.')
        : nothingToExport ? t('No submissions to export yet.') : undefined;

    const openDialog = () => {
        closePanel();
        toolbarItem.closeMenu();
        // Sensitive answers are opted into per export, never carried over from the last one.
        setIncludeSensitive(false);
        setOpen(true);
    };

    const sensitiveOption = hasSensitiveQuestions ? (
        <FormControlLabel
            control={(
                <Checkbox
                    size={'small'}
                    checked={includeSensitive}
                    onChange={(_event, checked) => setIncludeSensitive(checked)}
                />
            )}
            label={(
                <Stack direction={'row'} alignItems={'center'} gap={0.75}>
                    {/* The same lock the questions carry, so the toggle reads as the marker it lifts. */}
                    <SurveySensitiveMarker size={20}/>
                    {t('Include sensitive answers')}
                </Stack>
            )}
        />
    ) : undefined;

    const dialog = (
        <ServerTableExportDialog
            open={open}
            onClose={() => setOpen(false)}
            title={t('Submissions')}
            columns={columns}
            visibleColumnIds={allColumnIds}
            pageRowCount={pageRowCount}
            pageOffset={pageIndex * pageSize}
            totalRowCount={totalRowCount}
            extraOptions={sensitiveOption}
            onExport={(format, run) => void runExport(format, run)}
        />
    );

    if (toolbarItem.presentation === 'menu') {
        return (
            <>
                {/* A disabled menu item cannot carry a tooltip, so the reason is spelled out on the item. */}
                <MenuItem disabled={disabled} onClick={openDialog}>
                    <ListItemIcon>
                        {running
                            ? <CircularProgress size={18}/>
                            : <FileDownloadOutlinedIcon fontSize={'small'} color={'primary'}/>}
                    </ListItemIcon>
                    <ListItemText primary={t('Export')} secondary={reason}/>
                </MenuItem>

                {dialog}
            </>
        );
    }

    return (
        <>
            {/* The span keeps the tooltip reachable while the button is disabled. */}
            <Tooltip title={reason ?? t('Export')}>
                <span>
                    <IconButton
                        size={'small'}
                        color={'primary'}
                        data-dashboard-control=""
                        data-active={open || undefined}
                        aria-label={t('Export')}
                        aria-haspopup={'dialog'}
                        aria-busy={!!running}
                        disabled={disabled}
                        onClick={openDialog}
                        sx={serverTableControlSx}
                    >
                        {running
                            ? <CircularProgress size={18}/>
                            : <FileDownloadOutlinedIcon sx={serverTableControlIconSx}/>}
                    </IconButton>
                </span>
            </Tooltip>

            {dialog}
        </>
    );
};

export const SurveySubmissionsExport = defineServerTableToolbarItem(SurveySubmissionsExportComponent);

export default SurveySubmissionsExport;
